// Engagements list — server-side search/sort/filter + infinite pagination.
// Page size 100; controls send q/status/org_id/sort_by/order to the
// backend instead of the frontend filtering a loaded array.

import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Engagement } from "@/types/engagement";

export interface EngagementsFilters {
  q?: string;
  status?: string;
  /** Superuser-only — scopes the list to one tenant. The server returns
   * 403 for non-superusers; the UI only renders this control behind
   * `useAuthStore().user?.is_superuser`. */
  org_id?: string;
  sort_by?: string;
  order?: "asc" | "desc";
}

interface EngagementsPage {
  engagements: Engagement[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  next_offset: number | null;
}

const PAGE_SIZE = 100;

export function useEngagements(filters: EngagementsFilters = {}) {
  return useInfiniteQuery({
    queryKey: ["engagements", filters],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.status) params.set("status", filters.status);
      if (filters.org_id) params.set("org_id", filters.org_id);
      if (filters.sort_by) {
        params.set("sort_by", filters.sort_by);
        params.set("order", filters.order ?? "desc");
      }
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(pageParam));
      return api<EngagementsPage>(`/engagements/?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
}
